import React, {useState} from 'react'
import styled from 'styled-components'
import Container from '@material-ui/core/Container'
import Header from 'Event/template/SimpleBlog/Dashboard/Header'
import Menu from 'Event/template/SimpleBlog/Menu'
import {User} from 'auth/user'
import Footer from 'Event/template/SimpleBlog/Dashboard/Footer'
import {SimpleBlog, useSimpleBlogTemplate} from 'Event/template/SimpleBlog'
import {rgba} from 'lib/color'
import LanguageSelector from 'Event/LanguageSelector'
import {muiTheme} from 'lib/ui/theme'
import {ThemeProvider} from '@material-ui/core/styles'
import {withStyles} from '@material-ui/core/styles'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import {Font, useLoadFont} from 'lib/FontSelect'

export default function SimpleBlogPage(props: {
  user: User
  children: React.ReactElement | React.ReactElement[]
}) {
  const [menuVisible, setMenuVisible] = useState(false)
  const toggleMenu = () => setMenuVisible(!menuVisible)
  const template = useSimpleBlogTemplate()
  const {backgroundPosition, dashboardBackground: dashboard} = template
  const dashboardBackground = dashboard.image
    ? `url(${dashboard.image})`
    : '#FFFFFF'

  const backgroundRGBColor = dashboard
    ? rgba(dashboard.color || '#FFFFFF', dashboard.opacity / 100 || 0)
    : '#FFFFFF'

  const color = template.textColor
  const linkUnderline = template.linkUnderline
  const linkColor = template.linkColor
  const font = template.font

  useLoadFont(font)

  return (
    <ThemeProvider theme={muiTheme}>
      <Box
        data-testid="page container"
        background={dashboardBackground}
        backgroundPosition={dashboard.position}
        position={backgroundPosition}
        color={color}
        linkColor={linkColor}
        linkUnderline={linkUnderline}
        font={font}
      >
        <ColorOverlay color={backgroundRGBColor}>
          <Menu visible={menuVisible} toggle={toggleMenu} user={props.user} />
          <Header
            menuVisible={menuVisible}
            toggleMenu={toggleMenu}
            aria-label="header"
          />
          <Content>
            <StyledContainer maxWidth="lg">{props.children}</StyledContainer>
          </Content>
          <LanguageSelector />
          <Footer />
        </ColorOverlay>
      </Box>
    </ThemeProvider>
  )
}

const Box = styled.div<{
  background: string
  position: SimpleBlog['backgroundPosition']
  color: string
  linkColor: string
  linkUnderline: boolean
  font: Font | null
  backgroundPosition?: string
}>`
  height: auto;
  ${(props) => (props.font ? `font-family: ${props.font.family};` : '')}
  font-size: 17px;
  color: ${(props) => props.color};
  background: ${(props) => props.background};
  ${(props) =>
    props.position === 'bottom' &&
    `
      background-size: cover;
    `}
  ${(props) =>
    props.position === 'fixed' &&
    `
      background-attachment: fixed;
      background-size: cover;
    `}
  background-repeat: no-repeat;

  background-position: ${(props) => props.backgroundPosition ?? 'bottom'};
  backgroundPosition a {
    color: ${(props) => props.linkColor};
    text-decoration: none;

    &:hover {
      text-decoration: ${(props) =>
        props.linkUnderline ? 'underline' : 'none'};
    }
  }
`

const Content = styled.div`
  flex: 1;
  margin-bottom: 70px;
  display: flex;
`
const ColorOverlay = styled.div<{
  color: string
}>`
  background-color: ${(props) => props.color};
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

const StyledContainer = withStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
})(Container)

export const PageTitle = styled.h2`
  font-size: 42px;
  line-height: 1.5;
  text-transform: uppercase;
  text-align: center;
`
export const PageDescription = styled.div`
  margin: ${(props) => props.theme.spacing[5]};
  width: 90%;
  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    width: 70%;
  }
`

export const BackToDashboardLink = styled(RelativeLink)<{
  color?: string
}>`
  display: block;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.5;
  color: ${(props) => props.color || '#000000'};
`
